import React, { useState } from 'react';
import { Button, notification, Space } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import { IStock, ISearchStockReq, ISearchStockRes } from '@/interfaces/stock';
import { DeleteFilled, UploadOutlined } from '@ant-design/icons';
import { deleteStock, syncStock } from '@/adapters/stock';
import { IDataModal } from '@/interfaces/dataModal';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { mediaView } from '@/utils/mediaUtils';
import { twMerge } from 'tailwind-merge';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { isFullAccess, isWareHouse } from '@/utils/permissionUtils';
import { useMutation } from '@tanstack/react-query';

interface IProps {
  loading: boolean;
  criteria: ISearchStockReq;
  stock: ISearchStockRes;
  onDelete: (id: number) => void;
  onEditData: (item: IStock) => void;
  onUploadCoa: (id: number) => void;
  onShowStockMovement: (id: number) => void;
  onShowStockReserved: (item: IStock) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IStock> | SorterResult<IStock>[],
  ) => void;
}

const _stockModal: IDataModal<IStock> = {
  open: false,
};

const StockTable = ({
  loading,
  criteria,
  stock,
  onDelete,
  onShowStockMovement,
  onShowStockReserved,
  onUploadCoa,
  onTableChange,
}: IProps) => {
  const perFullAccess = isFullAccess();
  const perWareHouse = isWareHouse();
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IStock>>(_stockModal);

  const onDeleteStock = async () => {
    try {
      await deleteStock(Number(deleteModal?.id));
      notification.success({
        message: 'ลบสต็อกสำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._stockModal });
    } catch (error) {
      console.log(error);
    }
  };

  const mutations = useMutation({
    mutationFn: async (data: Partial<IStock>) =>
      await syncStock({ ...data, quantity: 0 }),
    mutationKey: ['syncStock'],
    onSuccess: data => {
      if (data.status !== 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'รับเข้าสต็อกสำเร็จ',
        });
        onDelete(0);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const columns: TableProps<IStock>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => (
        <Space>{(criteria.pageStart - 1) * 10 + (i + 1)}</Space>
      ),
    },
    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      width: 60,
      align: 'center',
      className: 'text-center',
    },
    {
      sorter: true,
      title: 'วันที่รับเข้า',
      dataIndex: 'stockDate',
      key: 'stockDate',
      width: 100,
      align: 'center',
      className: 'font-bold',
      render: _ => <Space>{formatDate(_)}</Space>,
    },
    {
      sorter: true,
      title: 'LOT No.',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 120,
      align: 'center',
      render: (_, rec) => (
        <Space
          onClick={() => onShowStockMovement(rec.id)}
          className="text-blue-700 underline cursor-pointer"
        >
          {_}
        </Space>
      ),
    },
    {
      sorter: true,
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold',
      width: 130,
      align: 'center',
      render: (_, rec) => (
        <Space
          onClick={() => navigate(`/product/${rec.productId}`)}
          className="text-gray-600 underline cursor-pointer"
        >
          {_}
        </Space>
      ),
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'productName',
      key: 'productName',
      className: 'font-bold',
    },
    {
      hidden: !criteria.iSample,
      title: 'ใช้สำหรับ',
      dataIndex: 'useFor',
      key: 'useFor',
      className: 'font-bold',
    },
    {
      hidden: !!criteria.iSample,
      title: 'COA',
      dataIndex: 'coa',
      key: 'coa',
      align: 'center',
      width: 240,
      render: (_, rec) => (
        <div className="flex flex-row gap-3 cursor-pointer justify-between">
          <div
            className={twMerge(rec.coaId && 'text-blue-600 underline')}
            onClick={() => (rec.coaId ? mediaView({ id: rec.coaId }) : false)}
          >
            {_ ?? 'ไม่พบไฟล์ COA'}
          </div>
          <div className="font-bold" onClick={() => onUploadCoa(rec.id)}>
            <UploadOutlined />
          </div>
        </div>
      ),
    },
    {
      hidden: !!criteria.iSample,
      sorter: true,
      title: 'Expire Date',
      dataIndex: 'coaExpiredDate',
      key: 'coaExpiredDate',
      width: 120,
      align: 'center',
      className: 'font-bold',
      render: _ => <Space>{formatDate(_)}</Space>,
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'font-bold text-gray-600',
      align: 'right',
      render: (_, rec) => (
        <Space>
          {formatNumber(Number(rec?.remain) / (rec.conversion || 1))}
        </Space>
      ),
      width: 120,
    },
    {
      hidden: !!criteria.iSample,
      title: 'Package',
      dataIndex: 'packageUnitName',
      key: 'packageUnitName',
      align: 'center',
      className: 'font-bold',

      width: 120,
    },
    {
      hidden: !!criteria.iSample,
      sorter: true,
      title: 'คงเหลือ',
      dataIndex: 'remain',
      key: 'remain',
      className: 'font-bold text-green-600',
      align: 'right',
      width: 120,
      render: _ => <Space>{formatNumber(_)}</Space>,
    },
    {
      hidden: !!criteria.iSample,
      sorter: true,
      title: 'จอง',
      dataIndex: 'reservedQuantity',
      key: 'reservedQuantity',
      className: 'font-bold text-orange-600',
      align: 'right',
      width: 100,
      render: (_, rec) => (
        <Space
          className="text-orange-600 underline cursor-pointer"
          onClick={() => onShowStockReserved(rec)}
        >
          {formatNumber(_ || 0)}
        </Space>
      ),
    },
    {
      hidden: !!criteria.iSample,
      sorter: true,
      title: 'สุทธิ',
      dataIndex: 'netRemain',
      key: 'netRemain',
      className: 'font-bold text-blue-600',
      align: 'right',
      width: 120,
      render: (_, rec) => (
        <Space>
          {formatNumber(
            Number(rec.remain || 0) - Number(rec.reservedQuantity || 0),
          )}
        </Space>
      ),
    },
    {
      hidden: !!criteria.iSample,
      title: 'หน่วยขาย',
      dataIndex: 'baseUnitName',
      key: 'baseUnitName',
      align: 'center',
      className: 'font-bold',
      width: 120,
    },
    {
      hidden: !criteria.iSample,
      title: 'ต้นทุน',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      className: 'font-bold text-green-600',
      align: 'right',
      width: 120,
      render: _ => formatNumber(_, 2),
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 120,
      hidden: !criteria?.iSample,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          {rec.iSample && !!rec.remain ? (
            <Button
              type="primary"
              size="small"
              loading={mutations.isPending}
              onClick={() => mutations.mutateAsync(rec)}
            >
              เบิกตัวอย่าง
            </Button>
          ) : (
            <div className="font-bold">เบิกแล้ว</div>
          )}
        </div>
      ),
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 40,
      hidden: (!perFullAccess && !perWareHouse) || !!criteria.iSample,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <>
            <DeleteFilled
              className="text-red-700"
              onClick={() =>
                setDeleteModal({
                  open: true,
                  item: rec,
                  id: rec.id,
                })
              }
            />
          </>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IStock>
        columns={columns}
        criteria={criteria}
        data={stock}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1700}
      />
      <DeleteConfirmModal
        content={`คุณต้องการลบสต็อก '${deleteModal?.item?.code}' ใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._stockModal });
        }}
        handleOk={() => {
          onDeleteStock();
        }}
        open={deleteModal?.open}
        title={`ลูกค้า '${deleteModal?.item?.code}'`}
      />
    </div>
  );
};

export default StockTable;
